import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setFeatureTableOpen } from "../redux/action/FeatureTable-actions";
import useWindowSize from "./useWindowSize";
import useViewPadding from "./View/useViewPadding";

const mobileOffset = 35;

const useDragSidebar = ({
  defaultWidth,
  ref,
  expandLimit = window.innerWidth / 1.3,
  show,
}) => {
  const { animatePadding } = useViewPadding("right");
  const { open: featureTableOpen } = useSelector(
    (state) => state.featureTableReducer
  );
  const [expand, setExpand] = useState(false);
  const { width } = useWindowSize();
  const printOpen = useSelector((state) => state.printWidget.open);
  const printLoading = useSelector((state) => state.printWidget.isLoading);

  const dispatch = useDispatch();

  const isMobile = useMemo(() => {
    return width <= 768;
  }, [width]);

  useEffect(() => {
    let timeout = 0;
    if (expand && show) {
      timeout = setTimeout(() => {
        animatePadding(defaultWidth);
      }, 200);
    }

    return () => {
      clearTimeout(timeout);
    };
  }, [defaultWidth, expand, show]);

  useEffect(() => {
    if (printOpen && !printLoading) {
      animatePadding(0);
    } else if (printOpen && printLoading) {
      animatePadding(0);
    } else if (!printOpen && !printLoading && expand) {
      animatePadding(defaultWidth);
    }
  }, [printOpen, printLoading]);

  useEffect(() => {
    if (!expand && ref.current) {
      expandToWidth(0);
    }
  }, [expand]);

  const handleExpand = (isOpen) => {
    requestAnimationFrame(() => {
      setExpand(isOpen);
      if (isOpen && featureTableOpen) {
        dispatch(setFeatureTableOpen(false));
      }
    });
  };

  const onArrowClick = (e) => {
    if (isMobile) {
      handleExpand(!expand);
      return;
    }
    e.stopPropagation();
    const viewDiv = document.querySelector("#viewDiv");
    viewDiv.style.transition = "all 0.5s";
    if (!expand) {
      expandToWidth(defaultWidth);
      handleExpand(!expand);
    } else {
      expandToWidth(0);
      handleExpand(!expand);
    }
  };

  const doDrag = useCallback(
    (e) => {
      if (!ref.current) return;
      e.preventDefault();
      ref.current.style.transition = "none";
      const appWidth = window.innerWidth;
      const width = appWidth - e.clientX;
      const viewDiv = document.querySelector("#viewDiv");
      viewDiv.style.transition = "none";

      if (width < defaultWidth) {
        ref.current.style.removeProperty("flex-basis");
        ref.current.style.removeProperty("width");
        return;
      }

      if (width > expandLimit) {
        expandToWidth(expandLimit);
        return;
      }

      expandToWidth(width);
      if (!expand) {
        handleExpand(true);
      }
    },
    [expand, expandLimit]
  );

  const stopDrag = useCallback(() => {
    if (!ref.current) return;
    const viewDiv = document.querySelector("#viewDiv");

    // Reset text selection after drag
    document.body.style.userSelect = "";
    document.body.style.mozUserSelect = "";
    document.body.style.msUserSelect = "";

    viewDiv.style.transition = "all 0.5s";
    ref.current.style.transition = "all 0.5s";
    document.documentElement.removeEventListener("mousemove", doDrag, false);
    document.documentElement.removeEventListener("mouseup", stopDrag, false);
  }, [doDrag]);

  const initDrag = useCallback(
    (e) => {
      if (isMobile) return;
      // e.preventDefault();

      document.body.style.userSelect = "none";
      document.body.style.mozUserSelect = "none";
      document.body.style.msUserSelect = "none";
      // if (e.target.id !== 'sidebar-resizer') return;
      document.documentElement.addEventListener("mousemove", doDrag, false);
      document.documentElement.addEventListener("mouseup", stopDrag, false);
    },
    [isMobile, doDrag, stopDrag]
  );

  const expandToWidth = (width) => {
    if (width > 0) {
      ref.current.style.flexBasis = `${width}px`;
      ref.current.style.width = `${width}px`;
      animatePadding(width);
    } else {
      ref.current.style.removeProperty("flex-basis");
      ref.current.style.removeProperty("width");
      animatePadding(0);
    }
  };

  return {
    expand,
    setExpand: handleExpand,
    initDrag,
    doDrag,
    stopDrag,
    onArrowClick,
    isMobile,
    mobileOffset,
    expandToWidth,
  };
};

export default useDragSidebar;
